import type { CSSProperties, ComponentType } from 'react'
import { DayPicker, getDefaultClassNames, type DayPickerProps } from 'react-day-picker'
import 'react-day-picker/style.css'

import { dateFnsLocale } from '@/shared/lib/datetime'
import { cn } from '@/shared/lib/utils'
import { resolveThemedExportSync } from '@/shared/theme/resolveThemedExportSync'

function CalendarSource({ className, classNames, ...props }: DayPickerProps) {
  const base = getDefaultClassNames()
  return (
    <DayPicker
      locale={dateFnsLocale()}
      showOutsideDays
      className={cn('p-3 text-foreground', className)}
      style={
        {
          '--rdp-accent-color': 'var(--color-primary)',
          '--rdp-accent-background-color': 'var(--v2-brand-soft)',
          '--rdp-range_middle-color': 'var(--color-foreground)',
          '--rdp-today-color': 'var(--v2-accent)',
          '--rdp-day-width': '2.4rem',
          '--rdp-day-height': '2.4rem',
          '--rdp-selected-border': '0',
        } as CSSProperties
      }
      classNames={{
        ...base,
        month: cn(base.month, 'relative'),
        month_caption: cn(
          base.month_caption,
          'relative flex items-center justify-center px-10 py-1 text-sm font-semibold text-foreground',
        ),
        nav: cn(base.nav, 'absolute inset-x-0 top-0 flex items-center justify-between'),
        button_previous: cn(
          base.button_previous,
          'inline-flex size-11 min-h-11 min-w-11 items-center justify-center rounded-md text-foreground',
          'hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        ),
        button_next: cn(
          base.button_next,
          'inline-flex size-11 min-h-11 min-w-11 items-center justify-center rounded-md text-foreground',
          'hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        ),
        weekday: cn(base.weekday, 'text-xs font-medium text-muted-foreground'),
        chevron: cn(base.chevron, 'size-4 fill-foreground'),
        today: cn(base.today, 'font-semibold'),
        ...classNames,
      }}
      {...props}
    />
  )
}

const ResolvedCalendar: ComponentType<DayPickerProps> = resolveThemedExportSync(
  'shared/components/ui/calendar.tsx',
  'Calendar',
  CalendarSource,
)

export function Calendar(props: DayPickerProps) {
  return <ResolvedCalendar {...props} />
}
